import { DeploymentRuleCondition, EntityRefUtil, formatDateWithUserTimezone, GetDeploymentRulesResponseDTO } from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import { FormBadge } from '@/components/form/form-badge';
import { ScheduleBadge } from '@/components/schedules/schedule-badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { cn, initials } from '@/lib/utils';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';

const columnHelper = createColumnHelper<GetDeploymentRulesResponseDTO['data'][number]>();

export const createDeploymentTableColumns = ({ canManageDeploymentRules = true }: { canManageDeploymentRules?: boolean } = {}) => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    canManageDeploymentRules &&
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <div className="flex w-full justify-center">
            <label className="-m-2 p-2">
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                aria-label="Select all"
              />
            </label>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex w-full justify-center">
            <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
            </label>
          </div>
        ),
        meta: {
          cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
        },
        enableSorting: false,
        enableHiding: false,
      }),
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Rule Name',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const value = getValue();

        return <div className="break-words whitespace-pre-line">{value}</div>;
      },
    }),
    columnHelper.accessor('number', {
      id: 'number',
      header: 'Rule ID',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const value = getValue();

        return <div className="break-words whitespace-pre-line">{EntityRefUtil.createDeploymentRuleRef(value)}</div>;
      },
      meta: {
        cellClassName: 'w-[100px]',
      },
    }),
    columnHelper.accessor('conditions', {
      id: 'conditions',
      header: 'Rule Conditions',
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ getValue }) => {
        const conditions = getValue();

        return (
          <div className="flex flex-col flex-wrap gap-1">
            {conditions?.map((condition, index) => {
              return (
                <div key={index} className="flex w-full flex-wrap items-center gap-1">
                  <span className="text-default-text text-sm font-medium">{attributeToLabel(condition.attribute)}:</span>{' '}
                  {Array.isArray(condition.value) ? (
                    <>
                      {condition.value.map((val, valIndex) => (
                        <span
                          key={valIndex}
                          className={cn(
                            'block max-w-full rounded p-[2px_5px] text-xs break-words whitespace-pre-line',
                            condition.operator === 'nin' && 'line-through',
                          )}>
                          {val.label}
                        </span>
                      ))}
                    </>
                  ) : (
                    <span className={cn('rounded p-[2px_5px] text-xs break-words', condition.operator === 'nin' && 'line-through')}>
                      {condition.value.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    }),
    columnHelper.accessor('assetCount', {
      id: 'assetCount',
      header: 'Assets',
      enableGrouping: false,
      enableColumnFilter: false,
      meta: {
        cellClassName: 'w-[80px]',
      },
    }),
    columnHelper.display({
      id: 'deployment',
      header: 'Deployment',
      enableGrouping: false,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        return (
          <div className="flex flex-col gap-1">
            {row.original.forms?.map((form) => <FormBadge className="break-words whitespace-pre-line" key={form.id} form={form} />)}
            {row.original.schedules?.map((schedule) => <ScheduleBadge className="break-words whitespace-pre-line" key={schedule.id} schedule={schedule} />)}
          </div>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: 'Created Date',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue }) => {
        const formattedValue = formatDateWithUserTimezone(new Date(getValue()), userTimezone);

        return formattedValue;
      },
      meta: {
        cellClassName: 'w-[120px]',
      },
    }),
    columnHelper.accessor('createdBy', {
      id: 'createdById',
      header: 'Created By',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ row }) => {
        const deploymentRule = row.original;
        const fullName = EntityRefUtil.createUserFullNameRef(deploymentRule.createdBy, 'Sitemate');
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(deploymentRule.createdBy?.firstName || '');

        const operatedByFullInitials = initials(fullName);

        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={operatedByFullInitials}
            avatarUrl={deploymentRule.createdBy?.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
    }),
  ].filter(Boolean) as AccessorKeyColumnDef<GetDeploymentRulesResponseDTO['data'][number]>[];
};

function attributeToLabel(attribute: DeploymentRuleCondition['attribute']) {
  return match(attribute)
    .with('assetType', () => 'Asset Type')
    .with('assetCategoryId', () => 'Asset Category')
    .with('assetTagId', () => 'Asset Tag')
    .with('assetManufacturer', () => 'Asset Manufacturer')
    .with('assetModel', () => 'Asset Model')
    .with('assetLocationId', () => 'Asset Location')
    .with('assetId', () => 'Asset ID')
    .exhaustive();
}
